import { useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError, isHttpStatus } from '../lib/http-error.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import { EditInvoiceModal } from '../components/InvoiceFormModal.js';
import { ManualRecurrencePanel } from '../components/InvoiceRecurrencePanel.js';
import { InvoiceScope } from '../components/InvoiceScope.js';
import { ConfirmModal } from './EngagementsPage.js';
import { INVOICE_STATUS_BADGE } from './InvoicesPage.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { formatMinorAmount, parseMajorAmount, normalizedMajorAmount, type InvoiceDetailOutput } from '@1financial/shared';
import { fmtDate } from '../lib/date.js';
import { DateInput } from '../components/DateInput.js';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const [showPayment, setShowPayment] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState<'cancel' | 'void' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sendWarning, setSendWarning] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const invoice = useQuery({
    queryKey: ['invoice', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/invoices/${id}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: InvoiceDetailOutput };
      return body.data;
    },
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  }

  const transition = useMutation({
    mutationFn: async (action: 'issue' | 'send' | 'resend' | 'cancel' | 'void') => {
      const res = await fetch(`/v1/invoices/${id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; warning?: string }>;
    },
    onSuccess: (body) => {
      setActionError(null);
      setSendWarning(body.warning ?? null);
      setConfirming(null);
      refresh();
    },
    onError: (err) => {
      setActionError((err as Error).message);
      setConfirming(null);
    },
  });

  if (isHttpStatus(invoice.error, 403)) return <ForbiddenPage />;
  if (invoice.isLoading) return <div className="text-sm text-muted">Loading invoice…</div>;
  if (invoice.error || !invoice.data) {
    return <div className="text-sm text-rose-300">Error: {(invoice.error as Error)?.message ?? 'Not found'}</div>;
  }
  const inv = invoice.data;
  const manage = canDo('billing.view') && canDo('billing.manage');
  const outstanding = inv.total.amount - inv.paidAmount;

  return (
    <div className="space-y-4 w-full min-w-0">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-white">{inv.invoiceNumber}</h1>
            <span className={`px-2 py-0.5 rounded text-xs ${INVOICE_STATUS_BADGE[inv.status] ?? 'bg-bg-hover text-white'}`}>
              {inv.status.replace(/_/g, ' ')}
            </span>
            {inv.editedAfterIssue && (
              <span className="px-2 py-0.5 rounded text-xs bg-amber-500/15 text-amber-300" title="Line items, due date or notes were changed after this invoice was issued — see the activity feed">
                edited after issue
              </span>
            )}
          </div>
          <p className="text-sm text-muted mt-1">
            {inv.crmClientId ? (
              <Link to={`/clients/${inv.crmClientId}`} className="text-accent hover:underline">{inv.crmClientName ?? inv.clientName ?? '—'}</Link>
            ) : (
              inv.clientName ?? '—'
            )}
            {inv.engagementId && (
              <>
                {' · '}
                <Link to={`/engagements/${inv.engagementId}`} className="text-accent hover:underline">
                  {inv.engagementTitle ?? 'Engagement'}
                </Link>
              </>
            )}
            {inv.complianceClientName && <>{' · '}{inv.complianceClientName} (compliance)</>}
          </p>
        </div>
        {manage && (
          <div className="flex flex-wrap gap-2">
            {inv.status === 'DRAFT' && (
              <button onClick={() => transition.mutate('issue')} disabled={transition.isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">Issue</button>
            )}
            {(inv.status === 'ISSUED' || inv.status === 'OVERDUE') && (
              <button onClick={() => transition.mutate('send')} disabled={transition.isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">Send to client</button>
            )}
            {(inv.status === 'SENT' || inv.status === 'OVERDUE') && (
              <button onClick={() => transition.mutate('resend')} disabled={transition.isPending} className="border border-border text-muted hover:text-white text-sm px-3 py-1.5 rounded-lg disabled:opacity-50" title="Re-email the client the current PDF — use after editing an already-sent invoice">Resend to client</button>
            )}
            {['ISSUED', 'SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(inv.status) && (
              <button onClick={() => setShowPayment(true)} className="bg-emerald-700 hover:bg-emerald-600 text-oncolor text-sm font-medium px-3 py-1.5 rounded-lg">Record payment</button>
            )}
            {['DRAFT', 'ISSUED', 'SENT', 'OVERDUE', 'PARTIALLY_PAID'].includes(inv.status) && (
              <button onClick={() => setEditing(true)} className="border border-border text-muted hover:text-white text-sm px-3 py-1.5 rounded-lg">Edit</button>
            )}
            {['DRAFT', 'ISSUED', 'SENT', 'OVERDUE'].includes(inv.status) && (
              <>
                <button onClick={() => setConfirming('cancel')} className="border border-border text-muted hover:text-white text-sm px-3 py-1.5 rounded-lg">Cancel</button>
                <button onClick={() => setConfirming('void')} className="border btn-ghost-danger text-sm px-3 py-1.5 rounded-lg">Void</button>
              </>
            )}
          </div>
        )}
      </header>
      {inv.manualScheduleId && <ManualRecurrencePanel scheduleId={inv.manualScheduleId} />}

      {actionError && <div className="text-sm text-rose-300">{actionError}</div>}
      {sendWarning && <div className="text-sm text-amber-300">{sendWarning}</div>}

      {/* PDF + public link (Invoicing Phase 2) */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={async () => {
            setPdfBusy(true);
            setActionError(null);
            try {
              const res = await fetch(`/v1/invoices/${inv.id}/pdf`, {
                headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
              });
              if (!res.ok) throw await httpError(res);
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${inv.invoiceNumber}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              setActionError((err as Error).message);
            } finally {
              setPdfBusy(false);
            }
          }}
          disabled={pdfBusy}
          className="border border-border text-muted hover:text-white text-sm px-3 py-1.5 rounded-lg disabled:opacity-50"
        >
          {pdfBusy ? 'Preparing…' : 'Download PDF'}
        </button>
        {manage && (
          <button
            onClick={async () => {
              setPdfBusy(true);
              setActionError(null);
              try {
                // Idempotent — ensures the PDF + token exist, then copies the link.
                const res = await fetch(`/v1/invoices/${inv.id}/pdf`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
                });
                if (!res.ok) throw await httpError(res);
                const body = await res.json() as { ok: true; data: { publicUrl: string } };
                await navigator.clipboard.writeText(body.data.publicUrl);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2000);
                refresh();
              } catch (err) {
                setActionError((err as Error).message);
              } finally {
                setPdfBusy(false);
              }
            }}
            disabled={pdfBusy}
            className="border border-border text-muted hover:text-white text-sm px-3 py-1.5 rounded-lg disabled:opacity-50"
          >
            {linkCopied ? 'Link copied' : 'Copy public link'}
          </button>
        )}
        {inv.publicToken && <span className="text-xs text-muted">Public link active</span>}
      </div>

      <section className="bg-bg-panel border border-border rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <div>
            <div className="text-xs text-muted">Issue date</div>
            <div className="text-white mt-1">{fmtDate(inv.issueDate)}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Due date</div>
            <div className="text-white mt-1">{fmtDate(inv.dueDate)}</div>
          </div>
          {inv.paidDate && (
            <div>
              <div className="text-xs text-muted">Paid date</div>
              <div className="text-white mt-1">{fmtDate(inv.paidDate)}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted">Subtotal</div>
            <div className="text-white mt-1">{formatMinorAmount(inv.subtotal.amount, true)} {inv.subtotal.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted">VAT</div>
            <div className="text-white mt-1">{formatMinorAmount(inv.tax.amount, true)} {inv.tax.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Total</div>
            <div className="text-xl font-semibold text-white mt-1">{formatMinorAmount(inv.total.amount, true)} {inv.total.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Paid</div>
            <div className="text-xl font-semibold text-emerald-300 mt-1">{formatMinorAmount(inv.paidAmount, true)} {inv.total.currency}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Outstanding</div>
            <div className={`text-xl font-semibold mt-1 ${outstanding > 0 ? 'text-amber-300' : 'text-muted'}`}>{formatMinorAmount(outstanding, true)} {inv.total.currency}</div>
          </div>
          {inv.reminderStage > 0 && inv.lastReminderAt && (
            <div>
              <div className="text-xs text-muted">Payment reminders</div>
              <div className="text-sm text-muted mt-1">Reminder {inv.reminderStage} of 3 sent {fmtDate(inv.lastReminderAt)}</div>
            </div>
          )}
        </div>
        {inv.notes && <p className="text-sm text-muted mt-3 border-t border-border pt-3 break-words max-w-prose">{inv.notes}</p>}
      </section>

      <section className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-bg-card border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-muted font-medium">Description</th>
              <th className="text-right px-4 py-3 text-muted font-medium">Amount</th>
              <th className="text-right px-4 py-3 text-muted font-medium">VAT</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {inv.lineItems.map((li, i) => (
              <tr key={i}>
                <td className="px-4 py-3 text-white break-words max-w-[280px]">{li.description}</td>
                <td className="px-4 py-3 text-right text-white">{formatMinorAmount(li.amount.amount, true)} {li.amount.currency}</td>
                <td className="px-4 py-3 text-right text-muted">{li.vatRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <InvoiceScope scope={inv.scopeSnapshot} />

      <section className="bg-bg-panel border border-border rounded-xl p-4">
        <h2 className="text-sm font-medium text-white mb-3">Payments</h2>
        {inv.payments.length === 0 ? (
          <p className="text-sm text-muted italic">No payments recorded.</p>
        ) : (
          <div className="divide-y divide-border">
            {inv.payments.map((p) => (
              <div key={p.id} className="py-2 flex items-center justify-between">
                <div>
                  <span className="text-white">{formatMinorAmount(p.amount.amount, true)} {p.amount.currency}</span>
                  <span className="text-xs text-muted ml-2">{p.method ?? ''}{p.note ? ` · ${p.note}` : ''}</span>
                </div>
                <span className="text-xs text-muted">{fmtDate(p.paidAt)}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {showPayment && (
        <RecordPaymentModal
          invoiceId={inv.id}
          outstanding={outstanding}
          currency={inv.total.currency}
          onClose={() => setShowPayment(false)}
          onDone={() => { setShowPayment(false); refresh(); }}
        />
      )}

      {editing && (
        <EditInvoiceModal
          invoice={inv}
          onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); refresh(); }}
        />
      )}

      {confirming && (
        <ConfirmModal
          title={confirming === 'void' ? 'Void invoice' : 'Cancel invoice'}
          message={`${confirming === 'void' ? 'Void' : 'Cancel'} ${inv.invoiceNumber}? Linked time entries will become unbilled again.`}
          onClose={() => setConfirming(null)}
          onConfirm={() => transition.mutate(confirming)}
          error={null}
          submitting={transition.isPending}
          confirmText={confirming === 'void' ? 'Void' : 'Cancel invoice'}
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}
    </div>
  );
}

export function RecordPaymentModal({ invoiceId, outstanding, currency, onClose, onDone }: {
  invoiceId: string;
  outstanding: number;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(formatMinorAmount(outstanding));
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const submission = useRef<{ payload: string; key: string } | null>(null);
  let minorAmount = 0, amountError: string | null = null;
  try {
    minorAmount = parseMajorAmount(amount);
    if (minorAmount <= 0) throw new Error('Payment must be greater than zero.');
    if (minorAmount > outstanding) throw new Error('Payment exceeds the outstanding balance.');
  } catch (error) { amountError = (error as Error).message; }

  const mutation = useMutation({
    mutationFn: async () => {
      if (amountError) throw new Error(amountError);
      setAmount(normalizedMajorAmount(amount));
      const payload = { amount: minorAmount, paidAt, ...(method ? { method } : {}), ...(note ? { note } : {}) };
      const fingerprint = JSON.stringify(payload);
      if (submission.current?.payload !== fingerprint) submission.current = { payload: fingerprint, key: crypto.randomUUID() };
      const res = await fetch(`/v1/invoices/${invoiceId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ ...payload, idempotencyKey: submission.current.key }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => { submission.current = null; onDone(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md mx-4 shadow-2xl animate-pop p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">Record payment</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Amount ({currency}) <span className="text-rose-400">*</span></label>
            <input
              type="text" inputMode="decimal" aria-label="Payment amount"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              onBlur={() => { try { setAmount(normalizedMajorAmount(amount)); } catch {} }}
              aria-invalid={!!amountError}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required
            />
            <p className="text-xs text-muted mt-1">Outstanding: {formatMinorAmount(outstanding, true)} {currency}</p>
            {amountError && <p role="alert" className="text-xs text-rose-400">{amountError}</p>}
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Paid at <span className="text-rose-400">*</span></label>
            <DateInput value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required />
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Method</label>
            <input value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Bank transfer, cash…" maxLength={100} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          {mutation.error && <p className="text-xs text-rose-400">{(mutation.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
            <button type="submit" disabled={mutation.isPending || !!amountError} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">
              {mutation.isPending ? 'Saving…' : 'Record payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
